import {
  NullableDateRangeInput,
  parseDateRange,
  toPrimitiveDateRangeInput,
} from '@superdispatch/dates';
import { FormikDateField, FormikDateFieldProps } from '@superdispatch/forms';
import { getIn, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { PostingRequestValuesDTO } from '../../data/dto/PostingRequestDTO';
import { getDefaultPickerTime } from './postingDateRules';

interface PostingDateFieldProps extends FormikDateFieldProps {
  preferredRange?: NullableDateRangeInput;
}

export function PostingDateField({
  name,
  preferredRange,
  ...props
}: PostingDateFieldProps) {
  const { initialValues } = useFormikContext<PostingRequestValuesDTO>();
  const today = useMemo(() => DateTime.local().startOf('day'), []);
  const [primitiveStart, primitiveFinish] =
    toPrimitiveDateRangeInput(preferredRange);
  const [highlightStart, highlightFinish] = useMemo(() => {
    const [start, finish] = parseDateRange([primitiveStart, primitiveFinish]);

    return [start?.startOf('day'), finish?.endOf('day')];
  }, [primitiveStart, primitiveFinish]);

  const initialTime = useMemo(() => {
    if (getIn(initialValues, name)) {
      return undefined;
    }

    return getDefaultPickerTime();
  }, [initialValues, name]);

  return (
    <FormikDateField
      {...props}
      name={name}
      format="JodaISO"
      placeholder="Select Date"
      CalendarProps={{
        initialTime,
        disabledDays: ({ dateValue }) => dateValue < today,
        highlightedDays: {
          blue:
            !highlightStart || !highlightFinish
              ? undefined
              : ({ dateValue }) =>
                  dateValue >= highlightStart && dateValue <= highlightFinish,
        },
      }}
    />
  );
}
